import { FileText, Star, FileEdit, SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { LibraryTab } from "./LibraryNav";

type TabOrSearch = LibraryTab | 'search';

interface LibraryEmptyStateProps {
  tab: TabOrSearch;
  onBuildPrompt?: () => void;
  searchQuery?: string;
}

interface EmptyStateContent {
  icon: typeof FileText;
  title: string;
  description: string;
  showCta: boolean;
  ctaText?: string;
}

export function LibraryEmptyState({ tab, onBuildPrompt, searchQuery }: LibraryEmptyStateProps) {
  const content: Record<TabOrSearch, EmptyStateContent> = {
    drafts: {
      icon: FileEdit,
      title: "No drafts in progress",
      description: "When you start building a prompt but don't finish, it will be saved here so you can continue later.",
      showCta: false,
    },
    recent: {
      icon: FileText,
      title: "No prompts yet",
      description: "Submit your first prompt to see it here. Your recent prompts will appear in this feed.",
      showCta: true,
      ctaText: "Build Your First Prompt",
    },
    favorites: {
      icon: Star,
      title: "No favorites yet",
      description: "Star prompts from the feed or your recent submissions to save them here for quick access.",
      showCta: false,
    },
    search: {
      icon: SearchX,
      title: "No results found",
      description: searchQuery 
        ? `We couldn't find anything matching "${searchQuery}". Try different keywords or browse your library.`
        : "Try searching for something specific like 'high-scoring emails' or 'strategy prompts'.",
      showCta: false,
    },
  };

  const { icon: Icon, title, description, showCta, ctaText } = content[tab];

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="p-4 rounded-full bg-muted/50 mb-4">
        <Icon className="h-10 w-10 text-muted-foreground/50" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">{description}</p>
      {showCta && onBuildPrompt && (
        <Button onClick={onBuildPrompt} className="gap-2">
          {ctaText}
        </Button>
      )}
    </div>
  );
}
